import { useAppSelector, useAppTranslation } from "@/utils";
import { Button, Divider, Drawer, Flex, Grid, Progress } from "@payping/web-components";
import { FC, PropsWithChildren, useState } from "react";
import { Close } from "@payping/icons";
import { clsx } from "clsx";
import PayGetOrderLayoutHeader from "./Header";
import PayGetOrderLayoutSteps from "./Steps";

interface LayoutProps {
  showHeaders?: boolean;
  currentStepError?: boolean;
}

const PayGetOrderLayout: FC<PropsWithChildren<LayoutProps>> = ({ children, currentStepError, showHeaders }) => {
  const { t } = useAppTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { xs, sm, md, lg, xl, xxl } = Grid.useBreakpoint();
  const desktop = lg || xl || xxl;
  const order = useAppSelector((state) => state.bnplConsumer.order);
  const totalSteps = order?.steps.filter((i) => i.isPublic);
  const currentStep = totalSteps.filter((i) => i.isPassed).length;

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };
  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  return (
    <>
      <div
        className={clsx([
          "tw:mx-auto tw:h-full tw:w-full",
          {
            "tw:max-w-full": xs,
            "tw:max-w-120 tw:py-12!": sm,
            "tw:max-w-120 tw:py-16.5!": md,
            "tw:max-w-3xl tw:py-16.5!": desktop,
          },
        ])}
      >
        <Flex
          vertical
          justify="space-between"
          className={clsx([
            "tw:relative tw:h-full tw:overflow-auto",
            { "tw:rounded-lg tw:border tw:border-solid tw:border-color-border": !xs },
          ])}
        >
          {showHeaders ? (
            <>
              <Flex vertical className="tw:px-4! tw:pb-4! tw:pt-5!" gap={"small"}>
                <PayGetOrderLayoutHeader />
                {desktop ? null : (
                  <Flex role="button" onClick={handleOpenDrawer} className="tw:cursor-pointer">
                    <Progress
                      status={currentStepError ? "exception" : "active"}
                      percent={((currentStep + 1) / totalSteps.length) * 100}
                      size="default"
                      format={() => (
                        <div>
                          {t("pay.getOrder.stepsHead.currentLevel", {
                            current: currentStep + 1,
                            total: totalSteps.length,
                          })}
                        </div>
                      )}
                    />
                  </Flex>
                )}
              </Flex>
              <Divider className="tw:m-0!" />
            </>
          ) : null}
          <Flex flex={1}>
            {desktop && showHeaders ? (
              <Flex className="tw:min-w-61.25 tw:max-w-61.25">
                <Flex className="tw:w-full tw:p-4!">
                  <PayGetOrderLayoutSteps currentStepError={currentStepError} />
                </Flex>
                <Divider className="tw:m-0! tw:h-full" type="vertical" />
              </Flex>
            ) : null}
            {children}
          </Flex>
        </Flex>
      </div>

      <Drawer
        onClose={handleCloseDrawer}
        open={openDrawer}
        title={
          <Flex className="tw:w-full" justify="end">
            <Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleCloseDrawer} />
          </Flex>
        }
        placement={"top"}
        closeIcon={false}
        classNames={{
          header: "tw:py-3! tw:px-4!",
        }}
      >
        <PayGetOrderLayoutSteps currentStepError={currentStepError} />
      </Drawer>
    </>
  );
};

export default PayGetOrderLayout;
